import {
  FillableElementPosition,
  IElement,
} from '@gohighlevel/ghl-proposals-common'
import { Ref, computed } from 'vue'

import { useRoute } from 'vue-router'
import { useFillableFieldsV2 } from './useFillableFieldsV2'

export interface PositionMapping {
  [key: string]: FillableElementPosition
}

export function useMarkerForImage(
  wrapperRef: Ref<HTMLDivElement>,
  imageRef: Ref<HTMLImageElement>,
  element: IElement
) {
  const route = useRoute()
  const debugMode = computed(() => {
    return route.query.debug === 'true'
  })
  const { getElementRefById } = useFillableFieldsV2()
  // Function to get positions for fillable elements relative to the image
  const getPositionsForFillables = () => {
    if (!imageRef.value || !wrapperRef.value) return {} as PositionMapping

    const mapping: PositionMapping = {}
    const fillableElements = element.children || []
    const imageRect = imageRef.value.getBoundingClientRect()

    fillableElements.forEach(fillable => {
      const fillableElement = getElementRefById(fillable.id)

      if (fillableElement) {
        const fillableRect = fillableElement.getBoundingClientRect()

        mapping[fillable.id] = {
          editorInstanceId: 'image-' + element.id,
          markerPos: {
            posFromLeftTop: {
              pos: 0,
              relativeCoords: {
                top: fillableRect.top - imageRect.top,
                bottom: fillableRect.bottom - imageRect.top,
                left: fillableRect.left - imageRect.left,
                right: fillableRect.right - imageRect.left,
              },
            },
          },
          elementRect: fillableRect,
          posToConsiderKey: 'posFromLeftTop',
        }
      }
    })

    return mapping
  }

  // Function to create and position markers
  const createMarkers = () => {
    if (!imageRef.value || !wrapperRef.value) return

    const positions = getPositionsForFillables()
    const imageRect = imageRef.value.getBoundingClientRect()
    const wrapperRect = wrapperRef.value.getBoundingClientRect()

    // Remove any existing markers
    imageRef.value
      .querySelectorAll('.image-position-marker')
      .forEach(marker => {
        marker.remove()
      })

    // Create markers for each fillable element
    Object.entries(positions).forEach(([id, position]) => {
      // Create marker container
      const marker = document.createElement('span')
      marker.className = 'image-position-marker'
      marker.setAttribute('data-marker-id', id)
      marker.style.cssText = `
      position: absolute;
      display: inline-block !important;
      width: ${debugMode.value ? '2px' : '0px'} !important;
      height: ${debugMode.value ? '2px' : '0px'} !important;
      max-width: inherit !important;
      max-height: inherit !important;
      background-color: ${debugMode.value ? 'red' : 'transparent'} !important;
      border-radius: 50% !important;
      vertical-align: middle !important;
      pointer-events: none !important;
      z-index: 9998 !important;
      transform: translateY(-50%) !important;
      left: ${imageRect.left - wrapperRect.left}px;
      top: ${imageRect.top - wrapperRect.top}px;
    `

      // Find and clone the original element
      const originalElement = getElementRefById(id)
      if (originalElement) {
        const clonedElement = originalElement.cloneNode(true) as HTMLElement
        const originalRect = originalElement.getBoundingClientRect()

        // Create a wrapper for the cloned element
        const wrapper = document.createElement('span')
        wrapper.style.cssText = `
        position: absolute !important;
        top: ${
          position.markerPos?.posFromLeftTop?.relativeCoords?.top
        }px !important;
        left: ${
          position.markerPos?.posFromLeftTop?.relativeCoords?.left
        }px !important;
        width: ${originalRect.width}px !important;
        height: ${originalRect.height}px !important;
        pointer-events: none !important;
        max-width: none !important;
        z-index: 9999 !important;
        background: ${debugMode.value ? 'blue' : 'transparent'} !important;
        overflow: visible !important;
        `

        // Style the cloned element
        clonedElement.style.cssText = `
        position: static !important;
        width: ${originalRect.width}px !important;
        height: ${originalRect.height}px !important;
        pointer-events: none !important;
        margin: 0 !important;
        padding: 0 !important;
        visibility: visible !important;
        opacity: 1 !important;
        `
        originalElement.style.opacity = '0'

        // In the case of checkbox width is added using v-bind and wont get applied on clone so set it manually
        const checkboxNode = clonedElement.querySelector(
          '.n-checkbox'
        ) as HTMLElement

        if (checkboxNode) {
          // Set dimensions for both the field and checkbox
          const width = `${originalRect.width}px`
          const height = `${originalRect.height}px`

          checkboxNode.style.cssText = `
            --n-border: 1px solid #FFFFFF !important;
            padding: 0 !important;
            width: ${width} !important;
            height: ${height} !important;
            --n-size: ${width} !important;
            display: block !important;
            --n-label-line-height: 1.6;
          --n-label-font-weight: 400;
          --n-size: 18px;
          --n-bezier: cubic-bezier(.4, 0, .2, 1);
          --n-border-radius: 2px;
          --n-border: 1px solid rgb(224, 224, 230);
          --n-border-checked: 1px solid #155EEF;
          --n-border-focus: 1px solid #155EEF;
          --n-border-disabled: 1px solid rgb(224, 224, 230);
          --n-border-disabled-checked: 1px solid rgb(224, 224, 230);
          --n-box-shadow-focus: 0 0 0 2px rgba(21, 94, 239, 0.3);
          --n-color: #FFF;
          --n-color-checked: #155EEF;
          --n-color-table: #fff;
          --n-color-table-modal: #fff;
          --n-color-table-popover: #fff;
          --n-color-disabled: rgb(250, 250, 252);
          --n-color-disabled-checked: rgb(250, 250, 252);
          --n-text-color: rgba(52, 64, 84, 1);
          --n-text-color-disabled: rgba(194, 194, 194, 1);
          --n-check-mark-color: #FFF;
          --n-check-mark-color-disabled: rgba(194, 194, 194, 1);
          --n-check-mark-color-disabled-checked: rgba(194, 194, 194, 1);
          --n-font-size: 15px;
          --n-label-padding: 0 8px;
          `
        }

        wrapper.appendChild(clonedElement)
        marker.appendChild(wrapper)
      }

      // Append to image instead of wrapper
      imageRef.value?.parentElement?.insertBefore(
        marker,
        imageRef.value.nextSibling
      )
    })
  }

  return {
    createMarkers,
    getPositionsForFillables,
  }
}
